import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { t } from 'i18next'

import { showToast } from '../components/Toasts'

/**
 * 🔹 Native (Capacitor) implementation
 * Saves file using Filesystem and opens Share sheet.
 */
export const downloadNative = async (blob: Blob, fileName: string) => {
  try {
    const base64 = await blobToBase64(blob)

    await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    })

    const { uri } = await Filesystem.getUri({
      path: fileName,
      directory: Directory.Documents,
    })

    await Share.share({
      title: t('downloads.title') || '',
      url: uri,
      dialogTitle: t('downloads.dialogTitle') || '',
    })

    showToast.success(t('downloads.successToast', { fileName }))
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    // Skip expected cancellation errors
    if (/cancel/i.test(message)) {
      showToast.info(t('downloads.shareCanceled'))
      return
    }

    showToast.error(
      t('downloads.nativeError', { error: error instanceof Error ? error.message : String(error) }),
    )
  }
}

/**
 * 🔹 Web (Browser) implementation
 * Uses <a download> for a simple reliable download.
 */
export const downloadWeb = async (blob: Blob, fileName: string) => {
  try {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    showToast.success(t('downloads.successToast', { fileName }))
  } catch (error) {
    showToast.error(
      t('downloads.webError', { error: error instanceof Error ? error.message : String(error) }),
    )
  }
}

/**
 * 🔹 Universal entry point
 * Automatically chooses native or web logic.
 */
export const downloadBlob = async (blob: Blob, fileName: string) => {
  if (Capacitor.isNativePlatform()) {
    await downloadNative(blob, fileName)
  } else {
    await downloadWeb(blob, fileName)
  }
}

/**
 * Downloads an image (base64 or data URL) using existing downloadBlob logic.
 */
export const downloadImage = async (base64: string, fileName: string) => {
  // base64 may be raw or already prefixed with "data:image/..."
  const cleanBase64 = base64.startsWith('data:image')
    ? base64.split(',')[1] // remove prefix if data URL
    : base64

  // Convert base64 → Blob
  const blob = base64ToBlob(cleanBase64, 'image/jpeg')

  // Delegate to your existing cross-platform download handler
  await downloadBlob(blob, fileName)
}

/**
 * Converts a Blob to a Base64 string (for Capacitor Filesystem).
 */
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = reject
    reader.onload = () => {
      const base64 = reader.result?.toString().split(',')[1]
      if (!base64) reject(new Error('Base64 conversion failed'))
      else resolve(base64)
    }
    reader.readAsDataURL(blob)
  })

/**
 * Converts base64 string to Blob
 */
function base64ToBlob(base64: string, contentType: string): Blob {
  const byteCharacters = atob(base64)
  const byteArrays: BlobPart[] = []
  const sliceSize = 512

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)
    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }
    byteArrays.push(new Uint8Array(byteNumbers))
  }

  return new Blob(byteArrays, { type: contentType })
}
